import { useMergeRefs } from "@floating-ui/react"
import classNames from "classnames"
import type {
  ComponentPropsWithoutRef,
  Ref,
  HTMLProps,
  MutableRefObject,
} from "react"
import {
  cloneElement,
  forwardRef,
  isValidElement,
} from "react"

import styles from "./ActionLink.module.scss"

// maps a key to a style class in ActionLink.module.scss
enum StylePreset {
  primary = "actionLinkPrimary",
  secondary = "actionLinkSecondary",
  destructive = "actionLinkDestructive",
}

type StylePresetKey = keyof typeof StylePreset
export const defaultStylePreset: StylePresetKey = "primary"

type LinkAttributes = ComponentPropsWithoutRef<"a">

export interface Props extends LinkAttributes {
  stylePreset?: StylePresetKey;
  asChild?: boolean;
}

// renders a rectangular <button />
export const ActionLink = forwardRef<HTMLAnchorElement, Props>(
  function ActionLink(props: Props, propRef: Ref<HTMLAnchorElement>) {
    const {
      children,
      asChild = false,
      stylePreset = defaultStylePreset,
      ...htmlLinkProps
    } = props

    const className = classNames(
      htmlLinkProps.className,
      styles[StylePreset[stylePreset]],
    )

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const childrenRef: MutableRefObject<any> = (children as any)?.ref
    const refWithChildrenRef = useMergeRefs([
      propRef,
      childrenRef,
    ])

    // `asChild` allows the user to pass any element as the anchor
    if (asChild && isValidElement(children)) {

      return cloneElement(
        children,
        {
          ref: refWithChildrenRef,
          ...htmlLinkProps,
          ...children.props,
          className,
        } as HTMLProps<HTMLElement>,
      )
    }

    return (
      <a
        {...htmlLinkProps}
        ref={propRef}
        className={className}
      >
        <span className={styles.actionLinkText}>
          {children}
        </span>
      </a>
    )
  },
)
